import React from "react";
import { TFunction } from "i18next";
import { PencilIcon } from "@aureskonnect/react-ui";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import classnames from "classnames";
import { useDispatch } from "react-redux";

import { AppDispatch } from "../../../../../../redux/store";
import {
    setIsActionsClicked,
    setIsEdited,
    setSortedVisibilityEdited,
} from "../../../../../../redux/slices/fidelitySlice";
import { useFidelityNavigationGuard } from "../../../FidelityNavigationGuardContext";
import { onKeyboardActivate } from "@helpers/a11y";
import { CESAR_LOYALTY } from "@constants/index";

type Props = {
    row: FidelityRow;
    oneShop: boolean;
    isEdited: boolean;
    isActionsClicked: boolean;
    isFidelityArchived: boolean;
    isLoading: boolean;
    setShopId: (id: FidelityRow["shopId"]) => void;
    setFidelity: (row: FidelityRow) => void;
    setIsAddFidelityClicked: (v: boolean) => void;
    setLocalFilterActive: (v: boolean) => void;
    t: TFunction;
};

function warnSoftaveraRestriction(t: TFunction) {
    toast.info(
        t(
            "Please note, Softavera loyalty can only be modified by the franchise."
        ),
        {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        }
    );
}

export default function EditIconButton({
    row,
    oneShop,
    isEdited,
    isActionsClicked,
    isFidelityArchived,
    isLoading,
    setShopId,
    setFidelity,
    setIsAddFidelityClicked,
    setLocalFilterActive,
    t,
}: Props) {
    const dispatch = useDispatch<AppDispatch>();
    const { setOnConfirm } = useFidelityNavigationGuard();
    const isSoftaveraInOneShop =
        oneShop && row.fidelityChoiceIndex === CESAR_LOYALTY;

    const handleClick = () => {
        if (isEdited) {
            dispatch(setIsActionsClicked(!isActionsClicked));
            setOnConfirm(() => {
                dispatch(setIsEdited(false));
                setIsAddFidelityClicked(true);
                setShopId(row.shopId);
                setFidelity(row);
                setLocalFilterActive(false);
                if (isSoftaveraInOneShop) warnSoftaveraRestriction(t);
            });
        } else {
            setIsAddFidelityClicked(true);
            setFidelity(row);
            dispatch(setSortedVisibilityEdited(row.sortedVisibility));
            setLocalFilterActive(false);
            if (isSoftaveraInOneShop) warnSoftaveraRestriction(t);
        }
    };

    const className = classnames(
        "pointer__clz cmn_icn_FRedit",
        { "not-allowed-icon__clz": isFidelityArchived || isLoading },
        oneShop ? "mr-2" : "ml-3"
    );

    return (
        <Tooltip title={isFidelityArchived ? "" : t("Edit")}>
            <div
                data-testid={`fidelity-franchise-edit-icon-${row.Id}`}
                role="button"
                tabIndex={isFidelityArchived ? -1 : 0}
                aria-label={t("Edit")}
                aria-disabled={isFidelityArchived}
                onKeyDown={onKeyboardActivate(handleClick)}
            >
                <PencilIcon
                    onClick={handleClick}
                    height={25}
                    width={25}
                    style={{ cursor: "pointer" }}
                    className={className}
                />
            </div>
        </Tooltip>
    );
}
